import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Bell, Check } from 'lucide-react';
import { useNotifications } from './NotificationContext';

export function NotificationBell() {
  const { points, loading } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = points.length;

  // Sluiten bij klik buiten de dropdown.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Aandachtspunten"
        aria-label={`Aandachtspunten${count > 0 ? ` (${count})` : ''}`}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-bmade-600 transition-colors hover:bg-bmade-100 hover:text-bmade-900 dark:text-bmade-300 dark:hover:bg-bmade-800 dark:hover:text-white"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-bmade-200 bg-white shadow-lg dark:border-bmade-700 dark:bg-bmade-900">
          <div className="border-b border-bmade-200 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-bmade-600 dark:border-bmade-800 dark:text-bmade-400">
            Aandachtspunten {count > 0 && `(${count})`}
          </div>
          <div className="max-h-96 overflow-auto">
            {loading ? (
              <div className="px-3 py-6 text-center text-xs text-bmade-500">Laden...</div>
            ) : count === 0 ? (
              <div className="flex flex-col items-center gap-1 px-3 py-6 text-center text-xs text-bmade-500">
                <Check className="h-5 w-5 text-emerald-500" />
                Geen openstaande aandachtspunten.
              </div>
            ) : (
              <ul className="divide-y divide-bmade-100 dark:divide-bmade-800">
                {points.map((p, i) => (
                  <li key={i}>
                    <Link
                      to={p.projectId ? `/projecten/${p.projectId}/inkoop` : '/inkoop'}
                      onClick={() => setOpen(false)}
                      className="flex gap-2 px-3 py-2 transition-colors hover:bg-bmade-50 dark:hover:bg-bmade-800/60"
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-bmade-950 dark:text-white">
                          {p.conflict.supplier} · {p.projectName}
                        </div>
                        <div className="text-[11px] leading-tight text-bmade-600 dark:text-bmade-400">
                          {p.conflict.message}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Link
            to="/inkoop"
            onClick={() => setOpen(false)}
            className="block border-t border-bmade-200 px-3 py-2 text-center text-xs font-medium text-accent-600 hover:bg-bmade-50 dark:border-bmade-800 dark:text-accent-400 dark:hover:bg-bmade-800/60"
          >
            Naar inkoop
          </Link>
        </div>
      )}
    </div>
  );
}
